import browser from 'detect-browser';
import { all, curry, map } from 'ramda';
import * as THREE from 'three';
import { fetchTextureImage, fetchHDRSourceData } from './asset';

// headless-gl and IE cause color quantization in our hdr.png's, so use original HDR instead
const preferHDRSource = (!__BROWSER__ || (__BROWSER__ && browser && browser.name === 'ie'));

const textures = {};
function checkTextureCache(id, image) {
  const cachedImage = textures[id] &&  textures[id].image;

  if (cachedImage && cachedImage._v === image._v) {
    return textures[id].value;
  }
  return null;
}

function cacheTexture(id, image, value) {
  if (textures[id]) {
    textures[id].value.dispose();
  }
  textures[id] = { image, value };
  return value;
}

export function getCubeMapTexture(store, renderer, image, textureCubeOnly, allowHDR) {
  const { maps, nodeId } = image;

  const texture = checkTextureCache(nodeId, image);
  if (texture) return texture;

  let useHDR = false;
  let useHDRSource = false;
  if (allowHDR && maps[0].preferHDR) useHDR = true;
  if (useHDR && preferHDRSource && maps[0].hdrSource) useHDRSource = true;

  const images = maps.map((imgPrimitive) => {
    const fetchImage = useHDRSource ? fetchHDRSourceData : fetchTextureImage;
    let img = fetchImage(store, imgPrimitive, true);

    // if hdr data, need to construct DataTexture in place of normal Image
    if (img && useHDRSource) {
      const dataTexture = new THREE.DataTexture(img.data, img.width, img.height);
      dataTexture.encoding = THREE.RGBEEncoding;
      dataTexture.generateMipmaps = false;
      dataTexture.type = THREE.UnsignedByteType;
      dataTexture.minFilter = THREE.NearestFilter;
      dataTexture.magFilter = THREE.NearestFilter;
      dataTexture.format = THREE.RGBEFormat;
      img = dataTexture;
    }

    return img;
  });

  if (!all(img => !!img, images)) {
    // console.log( "getCubeMapTexture early exit, as all images are not available.");
    return null;
  }

  const textureCube = new THREE.CubeTexture();

  // CubeTexure supports images as either instances of HTMLImageElement (for url-accessible image
  // elements) or THREE.DataTexture (for raw image data, such as our hdr source)
  textureCube.images = images;

  textureCube.flipY = false;
  textureCube.needsUpdate = true;
  if (useHDR) {
    textureCube.encoding = useHDRSource ? THREE.RGBEEncoding : maps[0].hdrEncoding;
    textureCube.generateMipmaps = false;
  }

  if (textureCubeOnly) return cacheTexture(nodeId, image, textureCube);

  const pmremFilter = useHDRSource ? THREE.NearestFilter : THREE.LinearFilter;

  // console.log( "Creating PMREMGenerator()");
  const pmremGenerator = new THREE.PMREMGenerator(textureCube, renderer.isMobile ? 20 : 64);
  pmremGenerator.update(renderer);

  pmremGenerator.cubeLods[0].texture.magFilter = pmremFilter;
  pmremGenerator.cubeLods[0].texture.minFilter = pmremFilter;

  // console.log( "Creating PMREMCubeUVPacker()");
  const pmremCubeUVPacker = new THREE.PMREMCubeUVPacker(pmremGenerator.cubeLods);
  pmremCubeUVPacker.update(renderer);

  pmremCubeUVPacker.CubeUVRenderTarget.texture.magFilter = pmremFilter;
  pmremCubeUVPacker.CubeUVRenderTarget.texture.minFilter = pmremFilter;

  // console.log( "PMREM Creation Done.");

  return cacheTexture(nodeId, image, pmremCubeUVPacker.CubeUVRenderTarget.texture);
}

export function getTextureImage(store, image) {
  if (!image) return;
  const nodeId = image.nodeId;

  if (!checkTextureCache(nodeId, image)) {
    if (image.maps) {
      map(curry(fetchTextureImage)(store), image.maps);
    } else {
      fetchTextureImage(store, image);
    }
  }
}

/*
 * Modifies the map being passed in.
 */
export function assignTexture(store, renderer, image, optionalThreemap, slot) {
  if (!image) {
    if (optionalThreemap) optionalThreemap.texture = null;
    return null;
  }

  // Only use hdr for maps that represent light sources. Not others, since we don't want to
  // *reflect* more than 1 light intensity. Note: Apparently hdr 'lightMap' not yet supported in
  // ThreeJS, so don't include it here (yet).
  const allowHDR = (slot === 'envMap' || slot === 'emissiveMap');

  if (image.maps) {
    return getCubeMapTexture(store, renderer, image, false, allowHDR);
  }

  let useHDR = false;
  let useHDRSource = false;
  if (allowHDR && image.preferHDR) useHDR = true;
  if (useHDR && preferHDRSource && image.hdrSource) useHDRSource = true;

  const uvOptions = Object.assign({}, image);
  const nodeId = image.nodeId;
  let texture = checkTextureCache(nodeId, image);

  if (!texture) {
    texture = useHDRSource ? new THREE.DataTexture() : new THREE.Texture();
    texture.meta = { nodeId };
    if (image.canvasOperations) {
      texture.meta.source = 'canvas operator';
      const ops = image.canvasOperations;

      const canvas = document.createElement('canvas');
      canvas.width = image.width;
      canvas.height = image.height;
      const context = canvas.getContext('2d');

      const ok = ops.reduce((ok, op) => {
        context.save();
        let ret = ok && op(context, store);
        context.restore();
        return ret;
      }, true);

      if (ok) texture.image = canvas;
    } else {
      texture.meta.source = 'image fetch';
      const fetchImage = useHDRSource ? fetchHDRSourceData : fetchTextureImage;
      texture.image = fetchImage(store, image);
    }

    if (!texture.image) {
      if (optionalThreemap) optionalThreemap.texture = null;
      return null;
    }

    texture.transparent = true;

    const textureWraps = {
      0: THREE.ClampToEdgeWrapping,
      1: THREE.RepeatWrapping,
      2: THREE.MirroredRepeatWrapping,
    };

    texture.wrapS = textureWraps[uvOptions.wrapU];
    texture.wrapT = textureWraps[uvOptions.wrapV];

    texture.generateMipmaps = useHDR ? false : uvOptions.generateMipMaps;
    texture.anisotropy = texture.generateMipmaps ? 16 : 1;
    texture.magFilter = useHDRSource ? THREE.NearestFilter : uvOptions.magFilter;
    texture.minFilter = useHDRSource ? THREE.NearestFilter : uvOptions.minFilter;
    texture.encoding = useHDR ? (userHDRSource ? THREE.RGBEEncoding : uvOptions.hdrEncoding) : THREE.sRGBEncoding;

    if ((THREE.Math.nearestPowerOfTwo(texture.image.width) !== texture.image.width) ||
        (THREE.Math.nearestPowerOfTwo(texture.image.height) !== texture.image.height)) {
      texture.wrapS = THREE.ClampToEdgeWrapping;
      texture.wrapT = THREE.ClampToEdgeWrapping;
      texture.generateMipmaps = false;
      texture.anisotropy = 1;
      texture.anisotropy = 1;
      texture.magFilter = useHDRSource ? THREE.NearestFilter : THREE.LinearFilter;
      texture.minFilter = useHDRSource ? THREE.NearestFilter : THREE.LinearFilter;
    }

    texture.needsUpdate = true;

    cacheTexture(nodeId, image, texture);
  }

  if (!optionalThreemap) {
    return texture;
  }

  optionalThreemap.texture = texture;

  optionalThreemap.uvTransform = true;
  optionalThreemap.uvOffset = new THREE.Vector2(uvOptions.uOffset, uvOptions.vOffset);
  optionalThreemap.uvRepeat = new THREE.Vector2(uvOptions.uTile, uvOptions.vTile);

  optionalThreemap.texelTransform = true;
  optionalThreemap.texelInvert = uvOptions.invert;
  optionalThreemap.texelScale = uvOptions.gain;
  optionalThreemap.texelOffset = uvOptions.brightness;
  //texture.gainPivot = uvOptions.gainPivot;
  //texture.gain = uvOptions.gain;
  //texture.brightness = uvOptions.brightness;

  //console.log('texture with image', texture);
  //textures[id] = texture;
  return optionalThreemap;
};

const initialStandardDefaults = {
  side: 2,
  emissive: { r: 0, g: 0, b: 0 },
  ambient: { r: 0, g: 0, b: 0 },
  vertexColors: 0,
  opacity: 1,
  transparent: false,
  specular: { r: 1, g: 1, b: 1 },
  normalScale: { x: 1, y: 1 },
  envMap: null,
  combine: 0,
  color: { r: 0.1803921568627451, g: 0.4, b: 0.8274509803921568 },
  metal: false,
  gloss: false,
  reflectivity: 1,
  refractionRatio: 0.98,
  shininess: 50,
};

export function primitiveToMaterial(store, renderer, data, previousMaterial) {
  let material = previousMaterial && previousMaterial instanceof THREE.MeshPhysicalMaterial ?
    previousMaterial : new THREE.MeshPhysicalMaterial({});

  if (!data) return material;

  if (data.type === 'MultiID') {
    const mats = [];
    for (let i = 0; i < 30; i++) {
      if (data['material'+i]) {
        mats.push(primitiveToMaterial(store, renderer, data['material'+ i]));
      }
      else {
        const defaultColor = { defaultColor: data.defaultColor };
        mats.push(primitiveToMaterial(store, renderer, defaultColor));
      }
    }
    material = new THREE.MultiMaterial(mats);
    return material;
  }
  // TODO: vertexColors, blend/combine?
  // FIXME: envMapScale??
  if (data.defaultColor) {
    material.color = data.defaultColor;
    return material;
  }

  if (!data.baseColor) {
    //console.log('Expected physical material, received: ', data.toJS());
    if (data.diffuseColor) material.color = data.diffuseColor;
    return material;
  }

  //side?
  //
  //name mapping
  //three.js: clara.io
  const propertyMap = {
    color: 'baseColor',
    //ambient: 'ambientColor2',
    bumpScale: 'bumpFactor',
    emissive: 'emissiveColor',
    falloff: 'baseFalloff',
    falloffColor: 'baseFalloffColor',
    metalness: 'metallic',
    roughness: 'roughness',
    clearCoat: 'clearCoat',
    clearCoatRoughness: 'clearCoatRoughness',
    opacity: 'opacityFactor', // opacityMap and opacityColor are not supported in Three.js
    falloffOpacity: 'opacityFalloffFactor',
    //specular: 'specularColor',
    //translucency: 'translucencyColor',
    transparent: 'baseMapTransparent',
  };

  const keys = [
    'depthTest', 'depthWrite',//,'roughness',
  ];

  // 'anisotropyMap','anisotropyRotationMap','--diffuseEnvMap','falloffMap', '+-metallicMap','+-opacityMap','reflectivityMap','specularMap','translucencyMap',
  // '--ambient','falloff','falloffColor','specular','translucency'
  //  'anisotropy','anisotropyRotation', 'clearCoat','clearCoatRoughness','+-metallic', 'translucencyNormalAlpha','translucencyNormalPower', 'translucencyViewAlpha','translucencyViewPower'

  //name mapping
  //three.js: clara.io
  const imageMaps = {
    map: 'baseMap',
    bumpMap: 'bumpMap',
    emissiveMap: 'emissiveMap',
    lightMap: 'lightMap',
    metalnessMap: 'metallicMap',
    normalMap: 'normalMap',
    alphaMap: 'opacityMap',
    roughnessMap: 'roughnessMap',
    falloffMap: 'baseFalloffMap',
    falloffAlphaMap: 'opacityFalloffMap',
  };

  function updateTexture(image, slot) {
    const mapTex = assignTexture(store, renderer, image, material[slot] || new THREE.MaterialMap(), slot);
    if (mapTex && !material[slot]) material[slot] = mapTex;
  };

  for (let image of Object.keys(imageMaps)) {
    updateTexture(data[imageMaps[image]], image + 'Slot');
  }

  //must be after updateTexture
  for (let to of Object.keys(propertyMap)) {
    material[to] = data[propertyMap[to]];
  }

  for (let key of keys) {
    material[key] = data[key];
  }

  if (data.emissiveScale) {
    material.emissive = material.emissive.clone();
    material.emissive.multiplyScalar(data.emissiveScale);
  }

  material.transparent = (material.opacity < 1.0) || !!data.opacityMap || data.baseMapTransparent || (data.opacityFalloffFactor < 1.0) || !!data.opacityFalloffMap;

  const specular = data.specularColor;
  material.reflectivity = (specular.r + specular.g + specular.b) * 0.33333;
  material.clearCoat *= 0.25;
  material.normalScale.x = (data.normalRedFlip ? -1 : 1) * data.normalFactor;
  material.normalScale.y = (data.normalGreenFlip ? -1 : 1) * data.normalFactor;

  material.blending = THREE.NormalBlending;
  material.premultipliedAlpha = true;
  material.side = THREE.DoubleSide;//( material.transparent ) ? THREE.DoubleSide : THREE.FrontSide; //temporary fix for steelcase

  const envMap = data.environment && data.environment.environmentMap;
  if (envMap) {
    updateTexture(envMap.specularCubeMap, 'envMap');
    material.envMapIntensity = (envMap.intensity !== undefined) ? envMap.intensity : 1.0;
  }

  //updateTexture(data.getIn(['envMap', 'specularCubeMap']), 'envMap');
  material.needsUpdate = true;

  //mat.envMapIntensity = 10;
  //mat.diffuseEnvMap = getTexture(store,  data.getIn(['envMap', 'irradianceCubeMap']));
  //console.log( 'mat', mat );
  //const standardMaterial =  new THREE.MeshStandardMaterial(mat);
  ////console.log( 'standardMaterial', standardMaterial );
  //return standardMaterial;
  return material;
}


//function multiIDMaterial(store, renderer, data, previousMaterial) {
//  const materials = reduce((list, i) => {
//    const mat = data.material${i};
//    if (mat) list.push(primitiveToMaterial(store, renderer, mat));
//    return list;
//  }, [], [0,1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21,22,23,24,25,26,27,28,29]);

//  return new THREE.MultiMaterial(materials);
//};



// WEBPACK FOOTER //
// ./src/translators/materials.js